import { useSyncExternalStore } from 'react';
import { createApiRef, useApi } from '@backstage/frontend-plugin-api';

export interface ChatDrawerState {
  open: boolean;
  /** Wide mode: the drawer takes more of the page, nothing else changes. */
  expanded: boolean;
  /** Text waiting to land in the composer, set by `open({ prompt })`. */
  prompt?: string;
}

/**
 * Opens and closes the chat drawer. UI state only — the conversation itself
 * lives in the drawer, so any plugin can open it without touching the stream.
 */
export interface ChatDrawerApi {
  /** `prompt` fills the composer without sending, leaving it editable. */
  open(options?: { prompt?: string }): void;
  close(): void;
  toggle(): void;
  setExpanded(expanded: boolean): void;
  /** Hands the pending prompt over once and clears it. */
  takePrompt(): string | undefined;
  getState(): ChatDrawerState;
  subscribe(listener: () => void): () => void;
}

export const chatDrawerApiRef = createApiRef<ChatDrawerApi>().with({
  id: 'plugin.llm-chat.drawer',
});

const STORAGE_KEY = 'llm-chat:drawer';

/** Open and width survive a reload; the pending prompt deliberately does not. */
export class ChatDrawerStore implements ChatDrawerApi {
  private state: ChatDrawerState;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly storage: Storage | null = safeStorage()) {
    this.state = { open: false, expanded: false, ...this.load() };
  }

  open(options?: { prompt?: string }) {
    this.update({ open: true, prompt: options?.prompt ?? this.state.prompt });
  }

  close() {
    this.update({ open: false });
  }

  toggle() {
    this.update({ open: !this.state.open });
  }

  setExpanded(expanded: boolean) {
    this.update({ expanded });
  }

  takePrompt() {
    const prompt = this.state.prompt;
    if (prompt !== undefined) this.update({ prompt: undefined });
    return prompt;
  }

  getState = () => this.state;

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private update(patch: Partial<ChatDrawerState>) {
    this.state = { ...this.state, ...patch };
    try {
      this.storage?.setItem(
        STORAGE_KEY,
        JSON.stringify({
          open: this.state.open,
          expanded: this.state.expanded,
        }),
      );
    } catch {
      // Private mode or a full quota: the drawer still works, it just forgets.
    }
    this.listeners.forEach(listener => listener());
  }

  private load(): Partial<ChatDrawerState> {
    try {
      const saved = JSON.parse(this.storage?.getItem(STORAGE_KEY) ?? '{}');
      return { open: saved.open === true, expanded: saved.expanded === true };
    } catch {
      return {};
    }
  }
}

function safeStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/** The drawer's state, re-rendering on every change. */
export function useChatDrawer(): ChatDrawerState & { api: ChatDrawerApi } {
  const api = useApi(chatDrawerApiRef);
  const state = useSyncExternalStore(api.subscribe, api.getState);
  return { ...state, api };
}
